import {
    Component,
    HostBinding,
    OnDestroy,
    OnInit,
    Renderer2
} from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { AppService } from '@services/app.service';
import { AuthService } from '@services/auth.service';

@Component({
    selector: 'app-forgot-password',
    templateUrl: './forgot-password.component.html',
    styleUrls: ['./forgot-password.component.scss']
})
export class ForgotPasswordComponent implements OnInit, OnDestroy {
    @HostBinding('class') class = 'container-fluid';
    public forgotPasswordForm: FormGroup;
    public resetPasswordForm: FormGroup;
    public showResetForm = false;   // toggle between forms
    public isAuthLoading = false;

    constructor(
        private renderer: Renderer2,
        private toastr: ToastrService,
        private appService: AppService,
        private authService: AuthService,
        private fb: FormBuilder
    ) { }

    ngOnInit(): void {
        this.renderer.addClass(
            document.querySelector('app-root'),
            'login-page'
        );
        this.forgotPasswordForm = new FormGroup({
            email: new FormControl(null, [Validators.required, Validators.email])
        });

        this.resetPasswordForm = this.fb.group({
            email: ['', [Validators.required, Validators.email]],
            code: ['', Validators.required],
            new_password: [
                '',
                [
                    Validators.required,
                    Validators.minLength(8),
                    // Must contain at least: 1 uppercase, 1 lowercase, 1 number, 1 special character
                    Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/)
                ]
            ],
            confirm_password: ['', Validators.required]
        }, { validators: this.passwordMatchValidator });
    }

    // Custom validator for confirm password
    passwordMatchValidator(control: AbstractControl) {
        const newPassword = control.get('new_password')?.value;
        const confirmPassword = control.get('confirm_password')?.value;
        return newPassword === confirmPassword ? null : { passwordMismatch: true };
    }

    // Forgot Password API
    forgotPassword() {
        if (this.forgotPasswordForm.valid) {
            this.isAuthLoading = true;
            const email = this.forgotPasswordForm.value.email;

            this.authService.forgotPassword(email).subscribe({
                next: (res) => {
                    this.toastr.success('OTP sent to your email!', 'Success');
                    this.isAuthLoading = false;
                    this.showResetForm = true; // show reset form after success
                    this.resetPasswordForm.patchValue({ email }); // auto-fill email
                },
                error: (err) => {
                    console.error("Forgot password error:", err); // 👈 for debugging
                    const errorMsg =
                        err.error?.details?.error || // handle AWS Cognito errors
                        'Something went wrong, please try again.';
                    this.toastr.error(errorMsg, 'Error');
                    this.isAuthLoading = false;
                }
            });
        }
    }

    // Reset Password API
    resetPassword() {
        if (this.resetPasswordForm.valid) {
            const { email, code, new_password, confirm_password } = this.resetPasswordForm.value;

            if (new_password !== confirm_password) {
                this.toastr.error("Passwords don't match", "Error");
                return;
            }

            this.isAuthLoading = true;
            this.authService.resetPassword({ email, code, new_password }).subscribe({
                next: (res) => {
                    this.toastr.success('Password reset successful!', 'Success');
                    this.isAuthLoading = false;
                    this.showResetForm = false; // back to login
                },
                error: (err) => {
                    this.toastr.error(err.error?.message || 'Something went wrong', 'Error');
                    this.isAuthLoading = false;
                }
            });
        }
    }

    ngOnDestroy(): void {
        this.renderer.removeClass(
            document.querySelector('app-root'),
            'login-page'
        );
    }
}
